/**
 * features-registration — point unique d'enregistrement des features.
 *
 * @remarks
 * `auth` + `settings` + `profile` + `skill-up` + `tech-tests` +
 * `candidate-results` + `cv` + `purchases` + admin (×3).
 * (hors AUTH_LOGIN qui est rendu par AuthLayout, pas par AppShell).
 */

import { SHELL_RENDERED_CONTEXTS, featureRegistry } from '@/app/registry';
import { shellManifest } from '@/app/shell-manifest';
import { authManifest } from '@/features/auth';
import { settingsManifest } from '@/features/settings';
import { profileManifest } from '@/features/profile';
import { skillUpManifest } from '@/features/skill-up';
import { techTestsManifest } from '@/features/tech-tests';
import { candidateResultsManifest } from '@/features/candidate-results';
import { cvManifest } from '@/features/cv';
import { purchasesManifest } from '@/features/purchases';
import { referralManifest } from '@/features/referral';
import {
  adminEvaluationsManifest,
  adminVariantsManifest,
  adminLibraryManifest,
  adminUsersManifest,
  adminTestsManifest,
  adminUserTestsManifest,
  adminPromoCodesManifest,
  adminSkillsManifest,
  adminSkillGroupsManifest,
  adminSkillGridsManifest,
  adminPositionsManifest,
  adminLlmConfigManifest,
  adminSkillUpManifest,
  adminCopilotManifest,
  adminDashboardManifest,
} from '@/features/admin';

export async function registerFeatures(): Promise<void> {
  // Raccourcis du shell — en premier : ils sont listés avant ceux des features.
  featureRegistry.register(shellManifest);
  featureRegistry.register(authManifest);
  featureRegistry.register(settingsManifest);
  featureRegistry.register(profileManifest);
  featureRegistry.register(techTestsManifest);
  featureRegistry.register(candidateResultsManifest);
  featureRegistry.register(cvManifest);
  featureRegistry.register(skillUpManifest);
  featureRegistry.register(purchasesManifest);
  featureRegistry.register(referralManifest);
  featureRegistry.register(adminEvaluationsManifest);
  featureRegistry.register(adminVariantsManifest);
  featureRegistry.register(adminLibraryManifest);
  featureRegistry.register(adminUsersManifest);
  featureRegistry.register(adminTestsManifest);
  featureRegistry.register(adminUserTestsManifest);
  featureRegistry.register(adminPromoCodesManifest);
  featureRegistry.register(adminSkillsManifest);
  featureRegistry.register(adminSkillGroupsManifest);
  featureRegistry.register(adminSkillGridsManifest);
  featureRegistry.register(adminPositionsManifest);
  featureRegistry.register(adminLlmConfigManifest);
  featureRegistry.register(adminSkillUpManifest);
  // Copilote d'authoring — onglet de panneau seul, derrière le flag `copilot`.
  featureRegistry.register(adminCopilotManifest);
  // Tableau de bord admin — données simulées, derrière le flag `admin-dashboard`.
  featureRegistry.register(adminDashboardManifest);

  // Dev-only : vitrine du design system (`/styleguide`). Import DYNAMIQUE gardé
  // par `import.meta.env.DEV` : en prod, `import.meta.env.DEV` est remplacé par
  // `false`, le bloc est éliminé (DCE) et le `import()` disparaît → Vite n'émet
  // AUCUN chunk pour la feature. La route n'existe pas en prod (404).
  if (import.meta.env.DEV) {
    const { styleguideManifest } = await import('@/features/styleguide');
    featureRegistry.register(styleguideManifest);
  }

  // La liste des contextes à couvrir est **dérivée** de `CONTEXTS`, plus
  // recopiée ici (SAAS-884) : une liste manuelle prend du retard sans que rien
  // ne le signale, et c'était déjà le cas — 56 entrées pour 58 contextes.
  // Les exemptions se déclarent dans `contexts.ts`, avec leur raison.
  featureRegistry.markInitialized(SHELL_RENDERED_CONTEXTS);
}
